import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  AlarmClock,
  CalendarCheck,
  Check,
  ChevronRight,
  CircleAlert,
  Clock3,
  FileQuestion,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from '../router';
import {api} from '../api';
import {Modal, PrimaryButton, QuietButton, SecondaryButton, Select, Surface, Textarea, TextInput} from '../design';
import {ActionItem, ActionReviewDecision, Contact} from '../types';
import {
  EmptyPanel,
  ErrorPanel,
  LoadingPanel,
  PageHeader,
  formatDateTime,
  formatRelativeDate,
} from '../components/PagePrimitives';
import {CalendarProposalReview} from '../components/CalendarProposalReview';

const icons: Record<ActionItem['kind'], typeof Sparkles> = {
  approval: ShieldCheck,
  missing_information: FileQuestion,
  interview: CalendarCheck,
  commitment: AlarmClock,
  stale_opportunity: Clock3,
  integration_incident: RefreshCw,
  discovery_review: Sparkles,
  manual_recovery: CircleAlert,
};

type Filter = 'open' | 'urgent' | 'approvals' | 'upcoming' | 'all';
type ContactDraft = {
  canonicalContactId: string;
  name: string;
  emails: string;
  phone: string;
  title: string;
};

const contactDraftFrom = (contact: Contact): ContactDraft => ({
  canonicalContactId: contact.id,
  name: contact.name,
  emails: contact.emails.join('\n'),
  phone: contact.phone ?? '',
  title: contact.title ?? '',
});

export const ActionInboxPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('open');
  const [calendarProposalId, setCalendarProposalId] = useState<string | null>(null);
  const [decisionAction, setDecisionAction] = useState<ActionItem | null>(null);
  const [contactDraft, setContactDraft] = useState<ContactDraft | null>(null);
  const [preferencePersonaId, setPreferencePersonaId] = useState('');
  const [preferenceValue, setPreferenceValue] = useState('');
  const actions = useQuery({queryKey: ['action-items'], queryFn: api.actionItems});
  const contacts = useQuery({
    queryKey: ['contacts'],
    queryFn: api.contacts,
    enabled: decisionAction?.relatedEntityType === 'contact_merge_proposal',
  });
  const personas = useQuery({
    queryKey: ['personas'],
    queryFn: api.personas,
    enabled: decisionAction?.relatedEntityType === 'preference_suggestion',
  });

  const update = useMutation({
    mutationFn: ({id, patch}: {id: string; patch: Pick<ActionItem, 'state'> & {snoozedUntil?: string}}) => api.updateActionItem(id, patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['action-items']});
      await queryClient.invalidateQueries({queryKey: ['bootstrap']});
    },
  });
  const decide = useMutation({
    mutationFn: ({id, decision}: {id: string; decision: ActionReviewDecision}) => api.decideActionItem(id, decision),
    onSuccess: async () => {
      setDecisionAction(null);
      setContactDraft(null);
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['action-items']}),
        queryClient.invalidateQueries({queryKey: ['bootstrap']}),
        queryClient.invalidateQueries({queryKey: ['contacts']}),
        queryClient.invalidateQueries({queryKey: ['personas']}),
        queryClient.invalidateQueries({queryKey: ['relationships']}),
        queryClient.invalidateQueries({queryKey: ['opportunities']}),
      ]);
    },
  });

  const candidateContacts = useMemo(() => {
    const candidateIds = new Set(decisionAction?.candidateContactIds ?? []);
    return (contacts.data?.items ?? []).filter((contact) => candidateIds.has(contact.id));
  }, [contacts.data, decisionAction]);
  useEffect(() => {
    if (decisionAction?.relatedEntityType !== 'contact_merge_proposal' || contactDraft || !candidateContacts[0]) return;
    setContactDraft(contactDraftFrom(candidateContacts[0]));
  }, [candidateContacts, contactDraft, decisionAction]);
  useEffect(() => {
    if (decisionAction?.relatedEntityType !== 'preference_suggestion' || preferencePersonaId || !personas.data?.items[0]) return;
    setPreferencePersonaId(personas.data.items[0].id);
  }, [decisionAction, personas.data, preferencePersonaId]);

  const filtered = useMemo(() => {
    const items = actions.data?.items ?? [];
    return items
      .filter((item) => {
        if (filter === 'all') return true;
        if (filter === 'open') return item.state === 'open';
        if (filter === 'urgent') return item.state === 'open' && item.priority === 'urgent';
        if (filter === 'approvals') return item.state === 'open' && item.kind === 'approval';
        return item.state === 'open' && Boolean(item.dueAt);
      })
      .sort((left, right) => {
        const priority = {urgent: 0, high: 1, normal: 2, low: 3};
        const priorityDifference = priority[left.priority] - priority[right.priority];
        if (priorityDifference !== 0) return priorityDifference;
        return new Date(left.dueAt ?? left.createdAt).getTime() - new Date(right.dueAt ?? right.createdAt).getTime();
      });
  }, [actions.data, filter]);
  const reviewAction = (item: ActionItem) => {
    if (item.resolutionTarget) {
      const query = new URLSearchParams({
        focus: item.resolutionTarget.focus,
        returnTo: item.resolutionTarget.returnTo,
      });
      navigate(`${item.resolutionTarget.route}?${query.toString()}`);
      return;
    }
    const entityType = (item as ActionItem & {relatedEntityType?: string}).relatedEntityType;
    if (entityType === 'preference_suggestion' || entityType === 'contact_merge_proposal') {
      setDecisionAction(item);
      setContactDraft(null);
      setPreferencePersonaId('');
      setPreferenceValue(item.suggestion
        ? item.suggestion.decision === 'dismissed' ? `Avoid ${item.suggestion.value}` : item.suggestion.value
        : '');
      decide.reset();
      return;
    }
    if (item.kind === 'manual_recovery' || entityType === 'extension_session') {
      const recoveryRef = item.relatedEntityId
        ? '&recovery=' + encodeURIComponent(item.relatedEntityId)
        : '';
      navigate('/settings?focus=extension' + recoveryRef + '#browser-companion');
      return;
    }
    if (entityType === 'calendar_proposal' && item.relatedEntityId) {
      setCalendarProposalId(item.relatedEntityId);
      return;
    }
    if (entityType === 'document') {
      navigate('/documents' + (item.relatedEntityId ? '?focus=' + encodeURIComponent(item.relatedEntityId) : ''));
      return;
    }
    if (entityType === 'opportunity' && item.relatedEntityId) {
      navigate('/opportunities/' + encodeURIComponent(item.relatedEntityId));
      return;
    }
    if (entityType === 'relationship') {
      navigate('/relationships' + (item.relatedEntityId ? '?focus=' + encodeURIComponent(item.relatedEntityId) : ''));
      return;
    }
    if (entityType === 'gmail_label_rule' || entityType === 'integration') {
      navigate('/settings');
      return;
    }
    if (item.kind === 'discovery_review') {
      navigate('/discover');
      return;
    }
    if (item.relatedEntityId && item.kind === 'missing_information') {
      navigate('/opportunities/' + encodeURIComponent(item.relatedEntityId));
      return;
    }
    navigate('/settings');
  };


  return (
    <>
      <PageHeader
        eyebrow="Nothing important slips"
        title="One queue for every decision and promise."
        description="Approvals, missing answers, interview preparation, overdue commitments, and integration failures arrive here with a clear next move."
      />

      <div className="pd-filter-tabs" role="tablist" aria-label="Action filters">
        {([
          ['open', 'Open'],
          ['urgent', 'Urgent'],
          ['approvals', 'Approvals'],
          ['upcoming', 'Due soon'],
          ['all', 'All'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={filter === value}
            className={filter === value ? 'is-active' : ''}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {actions.isLoading ? <LoadingPanel label="Loading actions" /> : null}
      {actions.isError ? <ErrorPanel error={actions.error} retry={() => void actions.refetch()} /> : null}
      {actions.data && filtered.length === 0 ? (
        <EmptyPanel
          title={filter === 'open' ? 'Your action inbox is clear.' : 'Nothing matches this filter.'}
          description={
            filter === 'open'
              ? 'PursuitDeck will surface the next approval, deadline, or blocked workflow here.'
              : 'Choose another filter to review the rest of your history.'
          }
        />
      ) : null}
      <div className="pd-action-list" aria-live="polite">
        {filtered.map((item) => {
          const Icon = icons[item.kind];
          const overdue = Boolean(item.dueAt && new Date(item.dueAt).getTime() < Date.now() && item.state === 'open');
          return (
            <Surface key={item.id} className={'pd-action-row priority-' + item.priority}>
              <div className="pd-action-icon" aria-hidden="true">
                <Icon size={20} />
              </div>
              <div className="pd-action-body">
                <div className="pd-action-labels">
                  <span>{item.kind.replaceAll('_', ' ')}</span>
                  <span>{item.priority}</span>
                  {overdue ? <strong>Overdue</strong> : null}
                </div>
                <h3>{item.title}</h3>
                <p>{item.detail}</p>
                <small>
                  {item.dueAt ? 'Due ' + formatDateTime(item.dueAt) : 'Added ' + formatRelativeDate(item.createdAt)}
                </small>
              </div>
              <div className="pd-action-controls">
                {item.kind === 'approval' ||
                item.kind === 'missing_information' ||
                item.kind === 'manual_recovery' ? (
                  <PrimaryButton
                    rightSection={<ChevronRight size={15} aria-hidden="true" />}
                    onClick={() => reviewAction(item)}
                  >
                    {item.kind === 'manual_recovery' ? 'Open recovery' : 'Review'}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    leftSection={<Check size={15} aria-hidden="true" />}
                    loading={update.isPending && update.variables?.id === item.id}
                    onClick={() => update.mutate({id: item.id, patch: {state: 'completed'}})}
                  >
                    Complete
                  </PrimaryButton>
                )}
                <label className="pd-snooze-control">
                  <span className="pd-visually-hidden">Snooze {item.title}</span>
                  <select
                    value=""
                    disabled={update.isPending && update.variables?.id === item.id}
                    onChange={(event) => {
                      const days = Number(event.currentTarget.value);
                      if (!days) return;
                      update.mutate({
                        id: item.id,
                        patch: {
                          state: 'snoozed',
                          snoozedUntil: new Date(Date.now() + days * 24 * 60 * 60 * 1_000).toISOString(),
                        },
                      });
                    }}
                  >
                    <option value="">Snooze…</option>
                    <option value="1">1 day</option>
                    <option value="3">3 days</option>
                    <option value="7">1 week</option>
                  </select>
                </label>
                <QuietButton onClick={() => update.mutate({id: item.id, patch: {state: 'dismissed'}})}>Dismiss</QuietButton>
              </div>
            </Surface>
          );
        })}
      </div>
      <CalendarProposalReview
        proposalId={calendarProposalId}
        opened={Boolean(calendarProposalId)}
        onClose={() => setCalendarProposalId(null)}
      />
      <Modal
        opened={decisionAction?.relatedEntityType === 'contact_merge_proposal'}
        onClose={() => setDecisionAction(null)}
        title="Review possible contact match"
        closeButtonProps={{'aria-label': 'Close contact match review'}}
        centered
        size="lg"
      >
        <div className="pd-modal-form">
          <p>Choose the record to keep, then review every canonical field. Related opportunities, applications, and conversations are rewired only after you approve.</p>
          {contacts.isLoading ? <LoadingPanel label="Loading contact records" /> : null}
          {contacts.isError ? <ErrorPanel error={contacts.error} retry={() => void contacts.refetch()} /> : null}
          {candidateContacts.length ? (
            <fieldset className="pd-choice-fieldset">
              <legend>Contact record to keep</legend>
              {candidateContacts.map((contact) => (
                <label key={contact.id} className="pd-check-control">
                  <input
                    type="radio"
                    name="canonical-contact"
                    checked={contactDraft?.canonicalContactId === contact.id}
                    onChange={() => setContactDraft(contactDraftFrom(contact))}
                  />
                  <span>
                    <strong>{contact.name}</strong>
                    {' — ' + ([contact.title, contact.emails.join(', '), contact.phone].filter(Boolean).join(' · ') || 'No additional fields')}
                  </span>
                </label>
              ))}
            </fieldset>
          ) : contacts.isSuccess ? <p className="pd-form-error" role="alert">The proposed contact records changed. Close this review and refresh the inbox.</p> : null}
          {contactDraft ? (
            <>
              <TextInput label="Canonical name" required value={contactDraft.name} onChange={(event) => setContactDraft({...contactDraft, name: event.currentTarget.value})} />
              <Textarea label="Canonical emails" description="One email address per line. Remove any address that should not remain on the contact." autosize minRows={2} value={contactDraft.emails} onChange={(event) => setContactDraft({...contactDraft, emails: event.currentTarget.value})} />
              <TextInput label="Canonical phone" value={contactDraft.phone} onChange={(event) => setContactDraft({...contactDraft, phone: event.currentTarget.value})} />
              <TextInput label="Canonical title" value={contactDraft.title} onChange={(event) => setContactDraft({...contactDraft, title: event.currentTarget.value})} />
            </>
          ) : null}
          {decide.isError ? <p className="pd-form-error" role="alert">{decide.error instanceof Error ? decide.error.message : 'The contact decision could not be saved.'}</p> : null}
          <div className="pd-form-actions pd-form-actions--between">
            <SecondaryButton
              type="button"
              loading={decide.isPending && decide.variables?.decision.decision === 'reject'}
              onClick={() => decisionAction && decide.mutate({id: decisionAction.id, decision: {kind: 'contact_merge', decision: 'reject'}})}
            >
              Keep separate
            </SecondaryButton>
            <PrimaryButton
              type="button"
              disabled={!contactDraft?.name.trim() || candidateContacts.length < 2}
              loading={decide.isPending && decide.variables?.decision.decision === 'approve'}
              onClick={() => decisionAction && contactDraft && decide.mutate({
                id: decisionAction.id,
                decision: {
                  kind: 'contact_merge',
                  decision: 'approve',
                  canonicalContactId: contactDraft.canonicalContactId,
                  canonical: {
                    name: contactDraft.name.trim(),
                    emails: contactDraft.emails.split(/\r?\n|,/).map((value) => value.trim()).filter(Boolean),
                    phone: contactDraft.phone.trim(),
                    title: contactDraft.title.trim(),
                  },
                },
              })}
            >
              Merge contacts
            </PrimaryButton>
          </div>
        </div>
      </Modal>
      <Modal
        opened={decisionAction?.relatedEntityType === 'preference_suggestion'}
        onClose={() => setDecisionAction(null)}
        title="Review learned preference"
        closeButtonProps={{'aria-label': 'Close learned preference review'}}
        centered
      >
        <div className="pd-modal-form">
          <p>{decisionAction?.detail}</p>
          <Select
            label="Persona to update"
            required
            value={preferencePersonaId}
            onChange={(value) => setPreferencePersonaId(value ?? '')}
            data={(personas.data?.items ?? []).map((persona) => ({value: persona.id, label: persona.name}))}
            disabled={personas.isLoading}
          />
          <Textarea
            label="Location strategy"
            description="Review or rewrite this suggestion. Saving returns the persona's search queries to draft for separate confirmation."
            required
            autosize
            minRows={3}
            value={preferenceValue}
            onChange={(event) => setPreferenceValue(event.currentTarget.value)}
          />
          {personas.isError ? <ErrorPanel error={personas.error} retry={() => void personas.refetch()} /> : null}
          {personas.isSuccess && personas.data.items.length === 0 ? <p className="pd-form-error" role="alert">Create a career persona before applying this suggestion.</p> : null}
          {decide.isError ? <p className="pd-form-error" role="alert">{decide.error instanceof Error ? decide.error.message : 'The preference decision could not be saved.'}</p> : null}
          <div className="pd-form-actions pd-form-actions--between">
            <SecondaryButton
              type="button"
              loading={decide.isPending && decide.variables?.decision.decision === 'reject'}
              onClick={() => decisionAction && decide.mutate({id: decisionAction.id, decision: {kind: 'preference', decision: 'reject'}})}
            >
              Reject suggestion
            </SecondaryButton>
            <PrimaryButton
              type="button"
              disabled={!preferencePersonaId || !preferenceValue.trim()}
              loading={decide.isPending && decide.variables?.decision.decision !== 'reject'}
              onClick={() => decisionAction && decide.mutate({
                id: decisionAction.id,
                decision: {
                  kind: 'preference',
                  decision: preferenceValue.trim() === (decisionAction.suggestion?.decision === 'dismissed'
                    ? `Avoid ${decisionAction.suggestion.value}`
                    : decisionAction.suggestion?.value) ? 'approve' : 'edit',
                  personaId: preferencePersonaId,
                  ...(preferenceValue.trim() === (decisionAction.suggestion?.decision === 'dismissed'
                    ? `Avoid ${decisionAction.suggestion.value}`
                    : decisionAction.suggestion?.value) ? {} : {value: preferenceValue.trim()}),
                } as ActionReviewDecision,
              })}
            >
              Save preference
            </PrimaryButton>
          </div>
        </div>
      </Modal>
      {update.isError ? (
        <p className="pd-toast pd-toast--error" role="alert">
          {update.error instanceof Error ? update.error.message : 'The action could not be updated.'}
        </p>
      ) : null}
    </>
  );
};
